// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { type ReactNode, useContext, useEffect } from "react";
import { AssistantLauncher } from "@/features/assistant/assistant-launcher";
import { AssistantProvider } from "@/features/assistant/assistant-provider";
import { usePageContext } from "@/features/assistant/page-context";
import { clearThread } from "@/features/assistant/persistence";
import { AuthContext } from "@/lib/auth/auth-provider";
import { useDebugMode } from "@/lib/debug-mode";
import { useFeatureFlags } from "@/lib/feature-flags";

/**
 * The provider is always mounted, so the thread survives flag flips and the showcase can
 * drive it. The launcher (and ⌘J) exists only behind the flag. Against the real service it
 * also needs the permission, and debug mode skips nothing: the service checks both. Against
 * the scripted mock, debug mode skips the flag.
 */
export function AssistantRoot({ children }: { children?: ReactNode }) {
  const pageContext = usePageContext();
  const { aiAssistant } = useFeatureFlags();
  const { debugMode } = useDebugMode();
  // Read the context directly (not useAuth) so the assistant never throws outside an AuthProvider.
  const auth = useContext(AuthContext);
  const ownerId = auth?.user?.publicId ?? null;
  // Optional call: tests and the showcase provide a partial AuthContext without hasPermission.
  const canUseAssistant = auth?.hasPermission?.("backoffice:assistant") ?? false;
  // Without a service URL the panel talks to the scripted mock, which checks nothing.
  const usesRealService = Boolean(import.meta.env.VITE_ASSISTANT_URL);

  // This subtree unmounts only when the signed-in shell goes away (logout, session end);
  // a reload never unmounts it, so the thread still survives refreshes.
  useEffect(function forgetThreadOnSignOut() {
    return () => clearThread();
  }, []);
  return (
    <AssistantProvider pageContext={pageContext} ownerId={ownerId}>
      {children}
      {(usesRealService ? aiAssistant && canUseAssistant : aiAssistant || debugMode) && <AssistantLauncher />}
    </AssistantProvider>
  );
}
